/**
 * Gentle motion for the Knowledge Archive canvas: what the level of detail
 * reveals fades in, and focusing a concept eases the rest of the map down.
 *
 * Without it, crossing a zoom threshold made concepts and their links POP
 * into existence, and hovering a concept snapped the whole map to 18% in one
 * frame. Both read as glitches rather than as the map responding.
 *
 * The canvas is redrawn by the library, not by React, so this hook keeps the
 * timing in refs and the renderer asks it for an alpha per node per frame.
 * `animating` is true while any fade is in flight; the graph turns off the
 * library's auto-pause for that window only, so a fade finishes even when the
 * pointer stops moving, and the canvas goes idle again afterwards.
 *
 * Under `prefers-reduced-motion` nothing fades: every alpha is final at once.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** How long a newly revealed concept takes to fade in. */
export const REVEAL_MS = 180;
/** How long the map takes to ease down around a newly focused concept. */
export const FOCUS_MS = 150;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)')?.matches === true;

/** Fast at first, settling at the end: arrival rather than a linear ramp. */
export const easeOut = (p: number) => 1 - (1 - p) * (1 - p);

/** 0..1 progress of an animation that began at `start`, eased. */
export function fadeProgress(start: number, now: number, ms: number): number {
  const p = (now - start) / ms;
  if (p >= 1) return 1;
  if (p <= 0) return 0;
  return easeOut(p);
}

export function useRevealFade({
  visibleIds,
  focusId,
  disabled = false,
}: {
  visibleIds: ReadonlySet<string>;
  focusId: string | null;
  disabled?: boolean;
}) {
  const revealedAt = useRef(new Map<string, number>());
  const previous = useRef<Set<string> | null>(null);
  const focusedAt = useRef(0);
  const previousFocus = useRef<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const [animating, setAnimating] = useState(false);

  const still = disabled || prefersReducedMotion();

  const pulse = useCallback((ms: number) => {
    setAnimating(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setAnimating(false), ms + 60);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Stamp every concept that has just become visible. The first set is the
  // opening view: it appears as it is, there is nothing to fade in from.
  useEffect(() => {
    const before = previous.current;
    previous.current = new Set(visibleIds);
    if (!before || still) return;
    const now = performance.now();
    let revealed = false;
    for (const id of visibleIds) {
      if (!before.has(id)) {
        revealedAt.current.set(id, now);
        revealed = true;
      }
    }
    if (revealed) pulse(REVEAL_MS);
  }, [visibleIds, still, pulse]);

  // Ease in only when focus starts. Moving from one concept to the next keeps
  // the map dimmed (re-easing would flash it bright between hovers), and
  // leaving focus restores at once, which reads as letting go.
  useEffect(() => {
    const was = previousFocus.current;
    previousFocus.current = focusId;
    if (focusId != null && was == null && !still) {
      focusedAt.current = performance.now();
      pulse(FOCUS_MS);
    }
  }, [focusId, still, pulse]);

  /** Multiplier for a concept's alpha: below 1 while it is fading in. */
  const revealAlpha = useCallback((id: string) => {
    const start = revealedAt.current.get(id);
    if (start == null) return 1;
    const p = fadeProgress(start, performance.now(), REVEAL_MS);
    if (p >= 1) revealedAt.current.delete(id);
    return p;
  }, []);

  /** 0..1, how far the map has eased down around the focus. */
  const focusProgress = useCallback(
    () =>
      still ? 1 : fadeProgress(focusedAt.current, performance.now(), FOCUS_MS),
    [still]
  );

  return { revealAlpha, focusProgress, animating };
}

/** Scale an `rgba(...)` colour's alpha by `factor` (a hex colour is opaque). */
export function scaleAlpha(colour: string, factor: number): string {
  if (factor >= 1) return colour;
  const m = /^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/.exec(colour);
  if (m) return `rgba(${m[1]}, ${m[2]}, ${m[3]}, ${Number(m[4]) * factor})`;
  const hex = /^#([0-9a-f]{6})$/i.exec(colour);
  if (!hex) return colour;
  const n = parseInt(hex[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${factor})`;
}
